/**
 * CompetitionContent - Main Content Area Component
 *
 * Single responsibility: Rendering the main competition content.
 * Handles header, info, questions list, and footer display.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import type { SelectionState } from '@/hooks/usePickemSelections';
import { queryKeys } from '@/api/react-query.config';
import GameCardFullWidth from '@/components/game/GameCardFullWidth';
import Breadcrumb from '@/components/shared/Breadcrumb';
import PickProgressBar from './PickProgressBar';
import { QuestionRenderer } from './QuestionRenderer';
import StickyActionBar from './StickyActionBar';
import {
  isTiebreakerQuestion,
  sortQuestionsWithTiebreakerLast,
} from '@/utils/questions/questionSorting';
import { useTenant } from '@/contexts/TenantContext';
import AdSlot from '@/components/ads/AdSlot';
import { generateAdDivId } from '@/config/ads.config';
import {
  getAnsweredQuestionsArray,
  countAnsweredQuestions,
} from '@/utils/questions/questionProgress';
import { getLeagueLogoUrl } from '@/utils/pickem-helpers';
import { getLocalizedField } from '@/utils/localized-field';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';
import { QuestionCardStyleProvider } from '@low6-pickem/ui';
import { SegmentedTabs } from '@/components/ui/tabs';
import { COMPETITION_PICKS_SCROLL_ID } from './competitionScroll';

interface CompetitionContentProps {
  // Data
  pickemDetails: PickemDetailResponse | undefined;
  competitionData: any; // TODO: Proper typing from domain.ts
  timeDisplay: string;
  currentUserId: string | undefined;

  // Selections
  selections: SelectionState;
  selectionResults?: Record<string, boolean>;
  selectionPoints?: Record<string, number>;

  // Handlers
  onTeamSelect: (questionId: string, teamId: string | undefined) => void;
  onPropSelect: (questionId: string, answer: 'yes' | 'no') => void;
  onOverUnderSelect: (questionId: string, answer: 'over' | 'under' | undefined) => void;
  onTieBreakerChange: (value: string) => void;
  onQuickPick: () => void;
  onFavouritePick: () => void;
  onClear: () => void;
  onSave: () => void;

  // State
  isContestClosed?: boolean;
  isViewOnly: boolean;
  isResultsMode: boolean;
  isSaving: boolean;
  showLeaderBoardOnLoad: boolean;
  /**
   * True once the user has any submitted selections for this pickem.
   * Drives the My Picks / Leaderboard tab strip — pre-entry the tabs
   * are hidden so the user lands directly on the picks interface.
   */
  hasEntered: boolean;
  /**
   * Return-to-lobby handler. Only used when the tenant has Pick'em full
   * navigation enabled (pre-DKPLAT-347 behaviour), where a breadcrumb back
   * to the Lobby replaces the in-game tab strip.
   */
  onBack?: () => void;
  /**
   * When absent/false the Popular Pick button and its autofill flow are hidden,
   * and pick-percentage badges on option cards are not shown.
   * Driven by `pickem.style_config.showPopularPicks`.
   */
  showPopularPicks?: boolean;
  /**
   * When absent/false the T&C confirmation checkbox is not rendered in the
   * entry confirmation panel and the Submit button is only gated by isSubmitting.
   * Driven by `pickem.style_config.showTermsCheckbox`.
   */
  showTermsCheckbox?: boolean;
  /**
   * When true, renders the contest-details info icon on the game card (used when
   * the header banner is hidden — DKPLAT-437).
   */
  showInfoOnGameCard?: boolean;
  onInfoClick?: () => void;
}

/**
 * Component that renders the main competition content area
 */
export const CompetitionContent: React.FC<CompetitionContentProps> = ({
  pickemDetails,
  competitionData,
  timeDisplay,
  selections,
  selectionResults,
  selectionPoints,
  currentUserId,
  onTeamSelect,
  onPropSelect,
  onOverUnderSelect,
  onTieBreakerChange,
  onQuickPick,
  onFavouritePick,
  onClear,
  onSave,
  isContestClosed = false,
  isViewOnly,
  isResultsMode,
  isSaving,
  showLeaderBoardOnLoad,
  hasEntered,
  onBack,
  showPopularPicks,
  showTermsCheckbox,
  showInfoOnGameCard,
  onInfoClick,
}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const handleCountdownComplete = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.pickem.all });
  }, [queryClient]);
  const { isAdsEnabled, adPlacements, isPlacementEnabled, pickemFullNavigation, tenant } =
    useTenant();
  const isCircusDaily = tenant?.code === 'cd';
  const showAdPlaceholders = import.meta.env.MODE === 'development';

  // Sort questions: regular questions first, tiebreaker last
  const sortedQuestions = sortQuestionsWithTiebreakerLast(pickemDetails?.pickem.questions || []);

  // Calculate answered questions for progress bar
  const answeredQuestions = getAnsweredQuestionsArray(sortedQuestions, selections);

  // Get indices of tiebreaker questions for progress bar star display
  const tiebreakerIndices = sortedQuestions
    .map((q, index) => (q.question_type?.toLowerCase() === 'tiebreaker' ? index : -1))
    .filter(index => index !== -1);

  // Check if any picks have been made
  const hasAnyPicks = countAnsweredQuestions(sortedQuestions, selections) > 0;

  // Picks count for the game card header ("nº picks") — exclude tiebreakers so
  // it matches lobby cards and ContestDetailsModal.
  const totalQuestions = sortedQuestions.filter(q => !isTiebreakerQuestion(q)).length;

  const [showLeaderBoard, setShowLeaderBoard] = useState(showLeaderBoardOnLoad);
  const [showResults, setShowResults] = useState(!showLeaderBoard);

  // Picks UI is always shown on the My Picks tab (no summary-first / Edit Picks gate).
  // Still hidden when the contest is closed and the user never entered.
  const isPicksEditingScroll = showResults && !(isContestClosed && !hasEntered);

  const onResultsClick = () => {
    setShowResults(true);
    setShowLeaderBoard(false);
  };

  const onLeaderboardClick = () => {
    setShowResults(false);
    setShowLeaderBoard(true);
  };

  const gameCardStyleConfig = pickemDetails?.pickem?.style_config?.game_card as
    | Record<string, unknown>
    | undefined;
  const bgGradientStart = gameCardStyleConfig?.bg_gradient_start as string | undefined;
  const bgGradientEnd = gameCardStyleConfig?.bg_gradient_end as string | undefined;
  const borderColor = gameCardStyleConfig?.border_color as string | undefined;
  const questionNumberBg = gameCardStyleConfig?.question_number_bg as string | undefined;
  const questionNumberFg = gameCardStyleConfig?.question_number_fg as string | undefined;

  const ddStyleConfig = pickemDetails?.pickem?.style_config?.daily_double as
    | Record<string, unknown>
    | undefined;
  const ddCardGradientStart = ddStyleConfig?.card_gradient_start as string | undefined;
  const ddCardGradientEnd = ddStyleConfig?.card_gradient_end as string | undefined;
  const ddCardBorderColor = ddStyleConfig?.card_border_color as string | undefined;
  const ddNumberBadgeBg = ddStyleConfig?.number_badge_bg as string | undefined;
  const ddNumberBadgeFg = ddStyleConfig?.number_badge_fg as string | undefined;
  const ddButtonBg = ddStyleConfig?.button_bg as string | undefined;
  const ddButtonBorder = ddStyleConfig?.button_border as string | undefined;
  const ddBackgroundImage = ddStyleConfig?.background_image as string | undefined;
  const ddLogoImage = ddStyleConfig?.logo_image as string | undefined;
  const ddSecondaryImage = ddStyleConfig?.secondary_image as string | undefined;

  const contestCardStyleConfig = pickemDetails?.pickem?.style_config?.contest_card as
    | Record<string, unknown>
    | undefined;
  const contestCardBgGradientStart = contestCardStyleConfig?.bg_gradient_start as
    | string
    | undefined;
  const contestCardBgGradientEnd = contestCardStyleConfig?.bg_gradient_end as string | undefined;
  const contestCardBorderColor = contestCardStyleConfig?.border_color as string | undefined;

  const logosStyleConfig = pickemDetails?.pickem?.style_config?.logos as
    | Record<string, unknown>
    | undefined;
  const confirmationLogoUrl = logosStyleConfig?.confirmation_logo as string | undefined;

  // Optional submit reminder shown above the Submit Picks button (DKPLAT-448).
  // style_config is freeform JSON, so guard the type — a non-string value must
  // not reach PickProgressBar (it calls .trim()).
  const entryStyleConfig = pickemDetails?.pickem?.style_config?.entry as
    | Record<string, unknown>
    | undefined;
  const rawSubmitMessage = entryStyleConfig?.submit_message;
  const submitMessage = typeof rawSubmitMessage === 'string' ? rawSubmitMessage : undefined;

  // Optional entry-confirmation popup copy (DKPLAT-515). Supports markdown-style
  // links so tenants (e.g. SkyPicks) can link out to their official rules.
  // Falls back to the default copy inside PickProgressBar when blank.
  const rawConfirmationMessage = entryStyleConfig?.confirmation_message;
  const confirmationMessage =
    typeof rawConfirmationMessage === 'string' ? rawConfirmationMessage : undefined;

  const gameCard = (
    <div className={isPicksEditingScroll ? '' : ''}>
      <GameCardFullWidth
        id={pickemDetails?.pickem.pickem_id || ''}
        title={competitionData.title}
        prize={
          pickemDetails?.pickem
            ? getLocalizedField(pickemDetails.pickem, 'prize_text', i18n.language) ||
              competitionData.prize ||
              ''
            : competitionData.prize || ''
        }
        state={competitionData.state}
        gameDate={new Date(competitionData.gameDate)}
        totalPicks={totalQuestions}
        leagueLogoUrl={getLeagueLogoUrl(pickemDetails?.pickem.sport as string)}
        totalEntries={competitionData.totalEntries}
        entryCost={t('game.labels.free')}
        competitionData={competitionData}
        myPrize={competitionData.myPrize}
        showLeaderBoard={showLeaderBoard}
        showResults={showResults}
        onViewLeaderboard={onLeaderboardClick}
        onViewResults={onResultsClick}
        bgGradientStart={contestCardBgGradientStart}
        bgGradientEnd={contestCardBgGradientEnd}
        borderColor={contestCardBorderColor}
        showInfoButton={showInfoOnGameCard}
        onInfoClick={onInfoClick}
        onCountdownComplete={
          competitionData.state === 'countdown' ? handleCountdownComplete : undefined
        }
      />
    </div>
  );

  const breadcrumbNav = onBack && (pickemFullNavigation || isCircusDaily) && (
    <Breadcrumb
      items={[{ label: t('tabs.lobby'), onClick: onBack }, { label: competitionData.title }]}
    />
  );

  // My Picks / Leaderboard under the game card (Figma 10136:15995).
  // Full-nav tenants hide this strip while the contest is still open;
  // once live or complete (isResultsMode) show it again so players can switch views.
  const picksTabStrip = hasEntered && (isResultsMode || !pickemFullNavigation) && (
    <div className="px-6 pt-4">
      <SegmentedTabs
        value={showLeaderBoard ? 'leaderboard' : 'myPicks'}
        onValueChange={value => {
          if (value === 'leaderboard') {
            setShowResults(false);
            setShowLeaderBoard(true);
            return;
          }
          setShowResults(true);
          setShowLeaderBoard(false);
        }}
        aria-label={t('a11y.contestViews')}
        items={[
          {
            value: 'myPicks',
            label: t('tabs.myPicks'),
            'data-test-id': 'game-tab-my-picks',
          },
          {
            value: 'leaderboard',
            label: t('tabs.leaderboard'),
            'data-test-id': 'game-tab-leaderboard',
          },
        ]}
      />
    </div>
  );

  return (
    <QuestionCardStyleProvider
      value={{
        bgGradientStart,
        bgGradientEnd,
        borderColor,
        questionNumberBg,
        questionNumberFg,
        ddCardGradientStart,
        ddCardGradientEnd,
        ddCardBorderColor,
        ddNumberBadgeBg,
        ddNumberBadgeFg,
        ddButtonBg,
        ddButtonBorder,
        ddBackgroundImage,
        ddLogoImage,
        ddSecondaryImage,
      }}
    >
      <div className="relative flex flex-col">
        {/* Breadcrumb / tabs / game card stay fixed unless the user is actively
            picking — then they scroll with the questions so the tile isn't pinned. */}
        {!isPicksEditingScroll && breadcrumbNav}
        {!isPicksEditingScroll && gameCard}
        {!isPicksEditingScroll && picksTabStrip}

        {showLeaderBoard && (
          <div className="p-0">
            <PickemLeaderboard
              pickemId={pickemDetails?.pickem.pickem_id}
              isOpen={showLeaderBoard}
              onClose={() => {}}
              currentUserId={currentUserId}
              contestStartUtc={pickemDetails?.pickem.start_utc}
            />
          </div>
        )}

        {isContestClosed && !hasEntered && showResults && (
          <div
            className="mx-4 mt-6 p-4 rounded-xl bg-surface border border-border/30 text-center"
            data-test-id="contest-closed-message"
          >
            <p className="text-foreground font-sans text-sm leading-relaxed">
              {t('game.contestClosed')}
            </p>
          </div>
        )}

        {isPicksEditingScroll && (
          <>
            <div id={COMPETITION_PICKS_SCROLL_ID}>
              {breadcrumbNav}
              {gameCard}
              {picksTabStrip}

              {/* Show Quick/Fav pick buttons */}
              {!isViewOnly && !isResultsMode && (
                <StickyActionBar
                  onQuickPick={onQuickPick}
                  onFavouritePick={onFavouritePick}
                  onClear={onClear}
                  hasAnyPicks={hasAnyPicks}
                  showFavouritePick={showPopularPicks === true}
                />
              )}

              {/* Questions List */}
              <div className="px-6 py-6 space-y-6">
                {(() => {
                  const nodes: React.ReactNode[] = [];
                  let nonTiebreakerCount = 0;

                  for (const question of sortedQuestions) {
                    const isTiebreaker = question.question_type?.toLowerCase() === 'tiebreaker';
                    const questionNumber = isTiebreaker ? undefined : nonTiebreakerCount + 1;

                    nodes.push(
                      <QuestionRenderer
                        key={question.question_id}
                        question={question}
                        questionNumber={questionNumber}
                        selectedTeams={selections.teams}
                        selectedProps={selections.props}
                        selectedOverUnder={selections.overUnder}
                        tieBreaker={selections.tieBreaker}
                        onTeamSelect={onTeamSelect}
                        onPropSelect={onPropSelect}
                        onOverUnderSelect={onOverUnderSelect}
                        onTieBreakerChange={onTieBreakerChange}
                        isViewOnly={isViewOnly}
                        selectionResults={isResultsMode ? selectionResults : undefined}
                        selectionPoints={isResultsMode ? selectionPoints : undefined}
                        showPopularPicks={showPopularPicks}
                      />
                    );

                    if (!isTiebreaker) {
                      nonTiebreakerCount += 1;

                      // Insert ads after pick 2 and pick 4, controlled per-tenant via placements config
                      const shouldShowAfter2 =
                        nonTiebreakerCount === 2 &&
                        (isAdsEnabled || showAdPlaceholders) &&
                        adPlacements &&
                        isPlacementEnabled('PICKS_AFTER_2');
                      const shouldShowAfter4 =
                        nonTiebreakerCount === 4 &&
                        (isAdsEnabled || showAdPlaceholders) &&
                        adPlacements &&
                        isPlacementEnabled('PICKS_AFTER_4');

                      if (shouldShowAfter2 || shouldShowAfter4) {
                        const placementKey = shouldShowAfter2 ? 'PICKS_AFTER_2' : 'PICKS_AFTER_4';
                        const index = shouldShowAfter2 ? 0 : 1;
                        nodes.push(
                          <div key={`ad-after-${nonTiebreakerCount}`} className="my-2">
                            <AdSlot
                              divId={generateAdDivId(adPlacements, placementKey, true, index)}
                              adPlacement={adPlacements[placementKey]}
                              placement={`picks-after-${nonTiebreakerCount}`}
                              className="rounded-lg overflow-hidden"
                            />
                          </div>
                        );
                      }
                    }
                  }

                  return nodes;
                })()}
              </div>
            </div>

            {/* Pick Progress Bar — pinned footer. Only when actually picking
              (not on the leaderboard tab, not in summary mode, not view-only,
              not results mode). */}
            {!isViewOnly && !isResultsMode && (
              <PickProgressBar
                answeredQuestions={answeredQuestions}
                tiebreakerIndices={tiebreakerIndices}
                onSubmit={onSave}
                className="max-w-5xl w-full mx-auto"
                isSubmitting={isSaving}
                confirmationLogoUrl={confirmationLogoUrl}
                showTermsCheckbox={showTermsCheckbox}
                submitMessage={submitMessage}
                confirmationMessage={confirmationMessage}
              />
            )}
          </>
        )}
      </div>
    </QuestionCardStyleProvider>
  );
};
